// FormSubmitBlock — the form's "send" button. Reads the form-state store keyed
// by formId, validates required inputs (empty values blocked with an inline
// error), and fires the action named by actionId. The action's run() receives
// the form values merged onto blockProps.formValues so it can read fields by
// name without having to know the form-state internals.
//
// Validation is light by design: any required input block (declared via
// blockProps.requiredFields, see below) that's empty in the form-state blocks
// the click. We don't try to enforce email regex etc. — the server validates
// addresses (EmailService rejects unparseable ones), and a stricter client gate
// usually just frustrates power users.

import { useState } from "react";
import {
  resolveContentSource,
  type ContentSource,
  type ContentSourceContext,
} from "./contentSource";
import type { BlockHostContext, BlockUniversalProps } from "./types";

export interface FormSubmitBlockProps extends BlockUniversalProps {
  host: BlockHostContext;
  /** ActionDef id to fire when clicked. */
  actionId: string;
  /** Button caption. */
  label?: string;
  /** Form id the button collects from. Defaults to "default" so single-form
   *  slots just work. */
  formId?: string;
  /** Field names that must be non-empty before the action fires. Comma-
   *  separated list on the wire; parsed here. */
  requiredFields?: string;
  /** Arbitrary extra props forwarded to the action verbatim — lets a submit
   *  block carry e.g. pdfLayoutId for the render-pdf-email action. Passed
   *  through alongside formValues so the action has both the operator-typed
   *  data AND the slot-mapping-baked config. */
  passthrough?: Record<string, unknown>;
  /** Glyph rendered after the label — defaults to "→". */
  trailingIcon?: string;
  /** Content source for the button caption (literal / dvar / from-*). */
  labelSource?: ContentSource;
  /** Live content-source context (series + selection), handed down by Slot. */
  sourceCtx?: ContentSourceContext;
}

export function FormSubmitBlock({
  host, actionId, label, formId, requiredFields, passthrough, trailingIcon,
  themeVariant, className, labelSource, sourceCtx,
}: FormSubmitBlockProps) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!actionId) {
    console.warn("[FormSubmitBlock] missing `actionId` prop; block skipped.");
    return null;
  }
  const fid = formId || "default";
  const action = host.actions.get(actionId);
  if (!action) {
    console.warn(`[FormSubmitBlock] action "${actionId}" not registered; block skipped.`);
    return null;
  }

  const click = async () => {
    setError(null);
    const values = host.readFormValues(fid);
    if (requiredFields) {
      const need = requiredFields.split(",").map(s => s.trim()).filter(Boolean);
      const missing = need.filter(k => !values[k] || !values[k].trim());
      if (missing.length > 0) {
        setError(`Required: ${missing.join(", ")}`);
        return;
      }
    }
    setBusy(true);
    try {
      action.run?.({
        ...(passthrough ?? {}),
        formId: fid,
        formValues: values,
      });
    } finally {
      setBusy(false);
    }
  };

  // Rules gate, additive to the requiredFields validation above: any
  // error-severity flag in the current overlay (scene-level or on any
  // instance) blocks submit — errors describe invalid configurations, and
  // submitting one would just hand the server a state it must reject.
  // Warnings/info never block.
  const overlay = host.rulesOverlay;
  const hasRuleErrors = Boolean(
    overlay &&
      (overlay.sceneFlags.some(f => f.severity === "error") ||
        Object.values(overlay.instances).some(io => io.flags.some(f => f.severity === "error"))),
  );

  const cls = "sd-form-submit" + (themeVariant ? ` sd-form-submit--${themeVariant}` : "") + (className ? " " + className : "");
  const arrow = trailingIcon ?? "→";
  const resolvedLabel = resolveContentSource(labelSource, label, sourceCtx ?? {});
  return (
    <div className={cls + "-wrap"}>
      {error && <div className="sd-form-submit__error" role="alert">{error}</div>}
      <button
        type="button"
        className={cls}
        onClick={click}
        disabled={busy || hasRuleErrors}
        title={hasRuleErrors ? "Resolve configuration errors first" : undefined}
      >
        <span>{busy ? "SENDING…" : (resolvedLabel ?? "SEND")}</span>
        {arrow && <span className="sd-form-submit__arrow" aria-hidden="true">{arrow}</span>}
      </button>
    </div>
  );
}
